/**
 * Packaging OS — Artifacts & exports viewer (final closure).
 *
 * Owner-test surface: list artifacts, inspect the honest status set
 * (draft/review/approval, gaps, evidence, versions), approve/reject,
 * export draft/final (final stays gated until approval) and download.
 */

import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  exportDownloadUrl,
  getArtifact,
  getArtifacts,
  getArtifactVersions,
  postArtifactDecision,
  postArtifactExport,
} from "../api/client";
import { Button } from "../components/Button";
import { StatusPill } from "../components/StatusPill";
import { strings } from "../i18n/strings";
import { useLocale } from "../i18n/useLocale";
import { AppLayout } from "./CatalogLayout";

type Row = Record<string, unknown>;

export function ArtifactsPage(): JSX.Element {
  const [locale] = useLocale();
  const t = strings(locale);
  const [params] = useSearchParams();
  const [items, setItems] = useState<Row[]>([]);
  const [selected, setSelected] = useState<string | null>(params.get("focus"));
  const [detail, setDetail] = useState<Row | null>(null);
  const [versions, setVersions] = useState<Row[]>([]);
  const [exports, setExports] = useState<Row[]>([]);
  const [notice, setNotice] = useState<string | null>(null);

  const refreshList = useCallback(async () => {
    const result = await getArtifacts();
    if (result.outcome === "ok") setItems(result.data?.items ?? []);
  }, []);

  const refreshDetail = useCallback(async (id: string) => {
    const [artifact, versionList] = await Promise.all([
      getArtifact(id),
      getArtifactVersions(id),
    ]);
    if (artifact.outcome === "ok" && artifact.data) {
      const data = artifact.data as Row;
      setDetail((data.artifact as Row) ?? data);
    }
    if (versionList.outcome === "ok") {
      setVersions(
        ((versionList.data as { items?: Row[] })?.items ?? []) as Row[],
      );
    }
  }, []);

  useEffect(() => {
    void refreshList();
  }, [refreshList]);

  useEffect(() => {
    if (selected) void refreshDetail(selected);
  }, [selected, refreshDetail]);

  async function decide(decision: "approve" | "reject"): Promise<void> {
    if (!selected) return;
    if (decision === "approve") {
      await postArtifactDecision(selected, "review", "reviewed in viewer");
    }
    const result = await postArtifactDecision(selected, decision, "via viewer");
    setNotice(result.outcome === "ok" ? null : result.errorMessage);
    await Promise.all([refreshDetail(selected), refreshList()]);
  }

  async function runExport(kind: "draft" | "final"): Promise<void> {
    if (!selected) return;
    const format = kind === "draft" ? "markdown" : "pdf";
    const result = await postArtifactExport(selected, { format, kind });
    if (result.outcome === "ok" && result.data) {
      setExports((prev) => [result.data!.export as unknown as Row, ...prev]);
      setNotice(null);
    } else {
      setNotice(
        result.status === 409 ? t.artifactExportFinalGated : result.errorMessage,
      );
    }
  }

  return (
    <AppLayout>
      <h1>{t.artifactsTitle}</h1>
      <p>{t.artifactsIntro}</p>
      {notice ? (
        <p role="alert" className="po-error">
          {notice}
        </p>
      ) : null}
      <div className="po-columns">
        <section aria-label={t.artifactsTitle} data-testid="artifact-list">
          <table className="po-table">
            <thead>
              <tr>
                <th>{t.artifactStatusLabel}</th>
                <th>Type</th>
                <th>Title</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr
                  key={String(row.id)}
                  onClick={() => setSelected(String(row.id))}
                  className={selected === row.id ? "po-row--active" : undefined}
                  data-testid={`artifact-row-${row.id}`}
                >
                  <td>
                    <StatusPill
                      tone={row.status === "approved" ? "ok" : "warn"}
                      label={String(row.status)}
                    />
                  </td>
                  <td>{String(row.artifact_type)}</td>
                  <td>{String(row.title ?? "")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {detail ? (
          <section aria-label={String(detail.title ?? "")} data-testid="artifact-detail">
            <h2>{String(detail.title ?? "")}</h2>
            <p>
              <StatusPill
                tone={detail.status === "approved" ? "ok" : "warn"}
                label={`${t.artifactStatusLabel}: ${String(detail.status)}`}
              />{" "}
              <StatusPill tone="warn" label={t.trustBannerShort} />
            </p>
            <p>
              {t.artifactVersions}: {versions.length} · {t.artifactSourceGaps}:{" "}
              {String(
                (versions[0] as Row | undefined)?.source_gap_count ?? "0",
              )}
            </p>
            <div className="po-row">
              <Button variant="secondary" onClick={() => void decide("approve")}>
                {t.artifactReviewApprove}
              </Button>
              <Button variant="secondary" onClick={() => void decide("reject")}>
                {t.artifactReviewReject}
              </Button>
              <Button variant="secondary" onClick={() => void runExport("draft")}>
                {t.artifactExportDraft}
              </Button>
              <Button variant="primary" onClick={() => void runExport("final")}>
                {t.artifactExportFinal}
              </Button>
            </div>
            <p className="po-disclaimer">{t.artifactExportFinalGated}</p>
            {exports.length > 0 ? (
              <ul data-testid="export-list">
                {exports.map((e) => (
                  <li key={String(e.id)}>
                    [{String(e.kind)}/{String(e.format)}]{" "}
                    <a href={exportDownloadUrl(String(e.id))}>
                      {t.artifactDownload}
                    </a>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        ) : null}
      </div>
    </AppLayout>
  );
}
